import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  AccountBalanceWallet,
  Assignment,
  CheckCircle,
  LocalShipping,
  MonetizationOn,
  Refresh,
  Search,
  Shield,
  Warning,
} from '@mui/icons-material';
import { apiFetch } from '../utils/apiFetch.js';

const money = (value) => new Intl.NumberFormat('en-CA', {
  style: 'currency',
  currency: 'CAD',
  maximumFractionDigits: 0,
}).format(Number(value || 0));

const statusColor = (status) => {
  if (['completed', 'released', 'released_to_mover', 'paid', 'approved', 'confirmed'].includes(status)) return 'success';
  if (['pending', 'held', 'escrowed', 'awaiting_customer_confirmation'].includes(status)) return 'warning';
  if (['disputed', 'failed', 'cancelled', 'refunded', 'payment_failed'].includes(status)) return 'error';
  return 'default';
};

const dateLabel = (value) => {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not set';
  return date.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
};

function MetricCard({ title, value, note, icon, tone = '#2563EB' }) {
  return (
    <Card elevation={0} sx={{ height: '100%', border: '1px solid #D9E2EC', borderRadius: 2, bgcolor: '#fff' }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
          <Box>
            <Typography variant="body2" sx={{ color: '#627D98', fontWeight: 700, mb: 0.75 }}>{title}</Typography>
            <Typography variant="h4" sx={{ color: '#102A43', fontWeight: 850, lineHeight: 1 }}>{value}</Typography>
          </Box>
          <Avatar sx={{ bgcolor: `${tone}1F`, color: tone }}>{icon}</Avatar>
        </Box>
        {note && <Typography variant="caption" sx={{ display: 'block', color: '#829AB1', mt: 1.5 }}>{note}</Typography>}
      </CardContent>
    </Card>
  );
}

function AdminOperationsDashboard({ onOpenMovers }) {
  const [tab, setTab] = useState(0);
  const [overview, setOverview] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [payments, setPayments] = useState([]);
  const [costExceptions, setCostExceptions] = useState([]);
  const [settlementQueue, setSettlementQueue] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [bookingSearch, setBookingSearch] = useState('');
  const [bookingStatus, setBookingStatus] = useState('all');
  const [paymentStatus, setPaymentStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paymentAction, setPaymentAction] = useState('release');
  const [actionReason, setActionReason] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const token = localStorage.getItem('token');
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const loadOverview = async () => {
    const res = await apiFetch('/api/admin/overview', { headers: authHeaders, credentials: 'include' });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to load overview');
    setOverview(data);
  };

  const loadBookings = async () => {
    const params = new URLSearchParams();
    if (bookingStatus !== 'all') params.set('status', bookingStatus);
    if (bookingSearch.trim()) params.set('search', bookingSearch.trim());
    const res = await apiFetch(`/api/admin/bookings${params.toString() ? `?${params}` : ''}`, { headers: authHeaders, credentials: 'include' });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to load bookings');
    setBookings(Array.isArray(data) ? data : []);
  };

  const loadPayments = async () => {
    const params = new URLSearchParams();
    if (paymentStatus !== 'all') params.set('escrowStatus', paymentStatus);
    const res = await apiFetch(`/api/admin/payments${params.toString() ? `?${params}` : ''}`, { headers: authHeaders, credentials: 'include' });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to load payments');
    setPayments(Array.isArray(data) ? data : []);
  };

  const loadAuditLogs = async () => {
    const res = await apiFetch('/api/admin/audit-logs', { headers: authHeaders, credentials: 'include' });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to load audit logs');
    setAuditLogs(Array.isArray(data) ? data : []);
  };

  const loadCostExceptions = async () => {
    const [claimsResponse, queueResponse] = await Promise.all([
      apiFetch('/api/admin/cost-exceptions', { headers: authHeaders, credentials: 'include' }),
      apiFetch('/api/admin/settlement-queue', { headers: authHeaders, credentials: 'include' }),
    ]);
    const claimsData = await claimsResponse.json();
    const queueData = await queueResponse.json();
    if (!claimsResponse.ok) throw new Error(claimsData.error || 'Failed to load cost exceptions');
    if (!queueResponse.ok) throw new Error(queueData.error || 'Failed to load settlement queue');
    setCostExceptions(Array.isArray(claimsData.claims) ? claimsData.claims : []);
    setSettlementQueue(Array.isArray(queueData.rows) ? queueData.rows : []);
  };

  const refresh = async () => {
    setLoading(true);
    try {
      await Promise.all([loadOverview(), loadBookings(), loadPayments(), loadAuditLogs(), loadCostExceptions()]);
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadBookings().catch(error => setSnackbar({ open: true, message: error.message, severity: 'error' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingStatus]);

  useEffect(() => {
    loadPayments().catch(error => setSnackbar({ open: true, message: error.message, severity: 'error' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentStatus]);

  const bookingStatusMap = useMemo(() => Object.fromEntries((overview?.bookingStatuses || []).map(item => [item._id || 'unknown', item.count])), [overview]);
  const heldPayments = payments.filter(payment => payment.escrowStatus === 'held');
  const releaseReady = heldPayments.filter(payment => payment.booking?.status === 'awaiting_customer_confirmation' || payment.jobCompletionStatus?.customerApproved);

  const openPaymentAction = (payment, action) => {
    setSelectedPayment(payment);
    setPaymentAction(action);
    setActionReason(action === 'hold' ? 'Completion needs MOVD review' : action === 'refund' ? 'Customer refund approved by MOVD' : '');
    setRefundAmount(action === 'refund' ? String(payment.totalAmount || payment.amount || '') : '');
  };

  const runPaymentAction = async () => {
    if (!selectedPayment) return;
    setActionLoading(selectedPayment._id);
    try {
      const body = paymentAction === 'release'
        ? {}
        : paymentAction === 'refund'
          ? { reason: actionReason, amount: Number(refundAmount) }
          : { reason: actionReason };
      const res = await apiFetch(`/api/admin/payments/${selectedPayment._id}/${paymentAction}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Failed to ${paymentAction} payment`);
      setSelectedPayment(null);
      setSnackbar({ open: true, message: paymentAction === 'release' ? 'Funds released to mover.' : paymentAction === 'refund' ? 'Refund issued.' : 'Payment placed on hold.', severity: 'success' });
      await refresh();
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setActionLoading('');
    }
  };

  const runCostExceptionAction = async (claim, action) => {
    setActionLoading(claim._id);
    try {
      const endpoint = action === 'retry'
        ? `/api/admin/cost-exceptions/${claim._id}/retry-settlement`
        : `/api/admin/cost-exceptions/${claim._id}/${action}`;
      const body = action === 'approve'
        ? { status: 'APPROVED', approvedAmount: Number(claim.approvedAmount || claim.claimedAmount || 0) }
        : {};
      const res = await apiFetch(endpoint, {
        method: action === 'approve' ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Failed to ${action} cost exception`);
      setSnackbar({ open: true, message: `Cost exception ${action} completed.`, severity: 'success' });
      await Promise.all([loadCostExceptions(), loadAuditLogs(), loadPayments()]);
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setActionLoading('');
    }
  };

  return (
    <Box sx={{ bgcolor: '#F4F7FB', minHeight: '100%', p: { xs: 1.5, md: 3 } }}>
      <Paper elevation={0} sx={{ p: { xs: 2.5, md: 4 }, mb: 3, borderRadius: 3, color: '#fff', background: 'linear-gradient(120deg, #102A43 0%, #176B87 100%)', boxShadow: '0 12px 28px rgba(16,42,67,0.18)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box>
            <Typography variant="overline" sx={{ color: '#9FE3D0', fontWeight: 800, letterSpacing: 1.5 }}>MOVD ADMIN</Typography>
            <Typography variant="h4" sx={{ fontWeight: 850, fontSize: { xs: '1.85rem', md: '2.5rem' } }}>Operations Command Center</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.76)', mt: 0.75 }}>Monitor bookings, movers, escrow, commissions, and release readiness from one place.</Typography>
          </Box>
          <Button variant="outlined" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Refresh />} onClick={refresh} disabled={loading} sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.45)' }}>
            Refresh
          </Button>
        </Box>
      </Paper>

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}><MetricCard title="Total Revenue" value={money(overview?.paymentTotals?.totalRevenue)} note="All recorded customer payments" icon={<MonetizationOn />} tone="#0E9F6E" /></Grid>
        <Grid item xs={12} sm={6} md={3}><MetricCard title="Escrow Held" value={money(overview?.paymentTotals?.heldEscrow)} note={`${heldPayments.length} held payment${heldPayments.length === 1 ? '' : 's'}`} icon={<Shield />} tone="#D97706" /></Grid>
        <Grid item xs={12} sm={6} md={3}><MetricCard title="Mover Liability" value={money(overview?.paymentTotals?.moverLiability)} note="Amount owed before release" icon={<AccountBalanceWallet />} tone="#2563EB" /></Grid>
        <Grid item xs={12} sm={6} md={3}><MetricCard title="Active Bookings" value={overview?.counts?.bookings || 0} note={`${bookingStatusMap.pending || 0} pending, ${bookingStatusMap.confirmed || 0} confirmed`} icon={<Assignment />} tone="#7C3AED" /></Grid>
      </Grid>

      <Paper elevation={0} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden', border: '1px solid #D9E2EC' }}>
        <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" scrollButtons="auto" sx={{ bgcolor: '#fff', '& .MuiTab-root': { fontWeight: 700, textTransform: 'none' } }}>
          <Tab label="Overview" />
          <Tab label="Bookings" />
          <Tab label="Escrow & Payments" />
          <Tab label="Cost Exceptions" />
          <Tab label="Mover Controls" />
          <Tab label="Audit Log" />
        </Tabs>
      </Paper>

      {loading ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}><CircularProgress /></Paper>
      ) : (
        <>
          {tab === 0 && (
            <Grid container spacing={2.5}>
              <Grid item xs={12} lg={7}>
                <Paper elevation={0} sx={{ p: 2.5, border: '1px solid #D9E2EC', borderRadius: 2 }}>
                  <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>Recent Bookings</Typography>
                  <Stack spacing={1.25}>
                    {(overview?.recentBookings || []).map(booking => (
                      <Box key={booking._id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, p: 1.5, border: '1px solid #E6EDF3', borderRadius: 2 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography fontWeight={800}>{booking.jobNumber || booking._id?.slice(-6)} - {booking.contactName || booking.user?.name || 'Customer'}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{booking.origin} to {booking.destination}</Typography>
                        </Box>
                        <Stack alignItems="flex-end" spacing={0.75}>
                          <Chip label={booking.status || 'unknown'} color={statusColor(booking.status)} size="small" />
                          <Typography variant="body2" fontWeight={800}>{money(booking.price)}</Typography>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              </Grid>
              <Grid item xs={12} lg={5}>
                <Paper elevation={0} sx={{ p: 2.5, border: '1px solid #D9E2EC', borderRadius: 2 }}>
                  <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>Release Queue</Typography>
                  {releaseReady.length === 0 ? (
                    <Alert severity="info">No escrow payments are ready for manual release right now.</Alert>
                  ) : (
                    <Stack spacing={1.25}>
                      {releaseReady.slice(0, 6).map(payment => (
                        <Box key={payment._id} sx={{ p: 1.5, border: '1px solid #E6EDF3', borderRadius: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                            <Box>
                              <Typography fontWeight={800}>{payment.booking?.jobNumber || payment._id.slice(-6)}</Typography>
                              <Typography variant="body2" color="text.secondary">{payment.mover?.name || 'Mover'} payout</Typography>
                            </Box>
                            <Typography fontWeight={850}>{money(payment.moverAmount)}</Typography>
                          </Box>
                          <Button size="small" variant="contained" sx={{ mt: 1 }} onClick={() => openPaymentAction(payment, 'release')}>Review Release</Button>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}

          {tab === 1 && (
            <Paper elevation={0} sx={{ p: 2.5, border: '1px solid #D9E2EC', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                <TextField size="small" placeholder="Search job, customer, route" value={bookingSearch} onChange={e => setBookingSearch(e.target.value)} InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }} />
                <Select size="small" value={bookingStatus} onChange={e => setBookingStatus(e.target.value)}>
                  {['all', 'pending', 'confirmed', 'in_progress', 'awaiting_customer_confirmation', 'completed', 'cancelled', 'refunded'].map(status => <MenuItem key={status} value={status}>{status}</MenuItem>)}
                </Select>
                <Button variant="outlined" onClick={loadBookings}>Search</Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow><TableCell>Job</TableCell><TableCell>Customer</TableCell><TableCell>Mover</TableCell><TableCell>Route</TableCell><TableCell>Date</TableCell><TableCell>Status</TableCell><TableCell align="right">Price</TableCell></TableRow></TableHead>
                  <TableBody>
                    {bookings.map(booking => <TableRow key={booking._id} hover><TableCell>{booking.jobNumber || booking._id.slice(-6)}</TableCell><TableCell>{booking.contactName || booking.user?.name || 'Customer'}<Typography variant="caption" display="block" color="text.secondary">{booking.contactEmail || booking.user?.email}</Typography></TableCell><TableCell>{booking.mover?.name || 'Unassigned'}</TableCell><TableCell sx={{ maxWidth: 300 }}>{booking.origin} to {booking.destination}</TableCell><TableCell>{dateLabel(booking.moveDate)}</TableCell><TableCell><Chip label={booking.status} color={statusColor(booking.status)} size="small" /></TableCell><TableCell align="right">{money(booking.price)}</TableCell></TableRow>)}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {tab === 2 && (
            <Paper elevation={0} sx={{ p: 2.5, border: '1px solid #D9E2EC', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                <Box>
                  <Typography variant="h6" fontWeight={800}>Escrow & Payments</Typography>
                  <Typography variant="body2" color="text.secondary">Review held payments, disputes, commissions, and mover payout readiness.</Typography>
                </Box>
                <Select size="small" value={paymentStatus} onChange={e => setPaymentStatus(e.target.value)}>
                  {['all', 'held', 'released', 'disputed', 'refunded', 'partial_refund'].map(status => <MenuItem key={status} value={status}>{status}</MenuItem>)}
                </Select>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow><TableCell>Payment</TableCell><TableCell>Booking</TableCell><TableCell>Mover</TableCell><TableCell>Customer</TableCell><TableCell>Status</TableCell><TableCell align="right">Total</TableCell><TableCell align="right">Commission</TableCell><TableCell align="right">Mover</TableCell><TableCell>Action</TableCell></TableRow></TableHead>
                  <TableBody>
                    {payments.map(payment => <TableRow key={payment._id} hover><TableCell>{payment._id.slice(-8)}</TableCell><TableCell>{payment.booking?.jobNumber || payment.booking?._id?.slice(-6) || 'N/A'}<Typography variant="caption" display="block" color="text.secondary">{dateLabel(payment.booking?.moveDate)}</Typography></TableCell><TableCell>{payment.mover?.name || 'Mover'}<Typography variant="caption" display="block" color="text.secondary">{payment.mover?.stripeConnectAccountId ? 'Stripe connected' : 'No payout account'}</Typography></TableCell><TableCell>{payment.booking?.contactName || payment.user?.name || 'Customer'}</TableCell><TableCell><Stack spacing={0.5}><Chip label={payment.escrowStatus || 'unknown'} color={statusColor(payment.escrowStatus)} size="small" /><Chip label={payment.paymentStatus || 'unknown'} color={statusColor(payment.paymentStatus)} size="small" variant="outlined" /></Stack></TableCell><TableCell align="right">{money(payment.totalAmount || payment.amount)}</TableCell><TableCell align="right">{money(payment.platformCommission)}</TableCell><TableCell align="right">{money(payment.moverAmount)}</TableCell><TableCell>{payment.escrowStatus === 'held' ? <Stack direction="row" spacing={1}><Button size="small" variant="contained" disabled={!payment.mover?.stripeConnectAccountId || actionLoading === payment._id} onClick={() => openPaymentAction(payment, 'release')}>{actionLoading === payment._id ? 'Working' : 'Release'}</Button><Button size="small" color="warning" variant="outlined" disabled={actionLoading === payment._id} onClick={() => openPaymentAction(payment, 'hold')}>Hold</Button><Button size="small" color="error" variant="outlined" disabled={actionLoading === payment._id || !payment.stripePaymentIntentId} onClick={() => openPaymentAction(payment, 'refund')}>Refund</Button></Stack> : <Typography variant="caption" color="text.secondary">No action</Typography>}</TableCell></TableRow>)}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {tab === 4 && (
            <Paper elevation={0} sx={{ p: 3, border: '1px solid #D9E2EC', borderRadius: 2 }}>
              <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>Mover Controls</Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>Approve movers, request documents, and set per-mover commission rates.</Typography>
              <Button variant="contained" startIcon={<LocalShipping />} onClick={onOpenMovers}>Open Mover Controls</Button>
            </Paper>
          )}

          {tab === 3 && (
            <Stack spacing={2.5}>
              <Paper elevation={0} sx={{ p: 2.5, border: '1px solid #D9E2EC', borderRadius: 2 }}>
                <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>Cost Exception Claims</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead><TableRow><TableCell>Booking</TableCell><TableCell>Mover</TableCell><TableCell>Claimed</TableCell><TableCell>Approved</TableCell><TableCell>Claim status</TableCell><TableCell>Payment</TableCell><TableCell>Action</TableCell></TableRow></TableHead>
                    <TableBody>
                      {costExceptions.map(claim => <TableRow key={claim._id} hover><TableCell>{claim.booking?.jobNumber || claim.booking?._id?.slice?.(-6) || 'N/A'}</TableCell><TableCell>{claim.mover?.name || 'Mover'}</TableCell><TableCell>{money(claim.claimedAmount)}</TableCell><TableCell>{money(claim.approvedAmount)}</TableCell><TableCell><Chip label={claim.status} size="small" color={statusColor(claim.status)} /></TableCell><TableCell><Stack spacing={0.5}><Chip label={claim.paymentStatus || 'pending'} size="small" color={statusColor(claim.paymentStatus)} /><Chip label={claim.settlementStatus || 'settlement_pending'} size="small" variant="outlined" color={statusColor(claim.settlementStatus)} /></Stack></TableCell><TableCell><Stack direction="row" spacing={1}>{['SUBMITTED', 'UNDER_REVIEW'].includes(claim.status) && <Button size="small" variant="contained" onClick={() => runCostExceptionAction(claim, 'approve')} disabled={actionLoading === claim._id}>Approve</Button>}{claim.paymentStatus === 'paid' && claim.status !== 'REFUNDED' && <Button size="small" color="error" variant="outlined" onClick={() => runCostExceptionAction(claim, 'refund')} disabled={actionLoading === claim._id}>Refund</Button>}{claim.paymentStatus === 'paid' && claim.settlementStatus !== 'settled' && <Button size="small" variant="outlined" onClick={() => runCostExceptionAction(claim, 'retry-settlement')} disabled={actionLoading === claim._id}>Retry</Button>}</Stack></TableCell></TableRow>)}
                    </TableBody>
                  </Table>
                </TableContainer>
                {costExceptions.length === 0 && <Alert severity="info" sx={{ mt: 2 }}>No cost exception claims found.</Alert>}
              </Paper>
              <Paper elevation={0} sx={{ p: 2.5, border: '1px solid #D9E2EC', borderRadius: 2 }}>
                <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>Additional-Charge Settlement Queue</Typography>
                <TableContainer><Table size="small"><TableHead><TableRow><TableCell>Booking</TableCell><TableCell>Customer</TableCell><TableCell>Mover</TableCell><TableCell>Amount</TableCell><TableCell>Payment</TableCell><TableCell>Settlement</TableCell><TableCell>Failure</TableCell></TableRow></TableHead><TableBody>{settlementQueue.map(row => <TableRow key={`${row.claimId || row.bookingId}-${row.createdAt}`}><TableCell>{row.bookingId?.toString?.().slice?.(-8) || 'N/A'}</TableCell><TableCell>{row.customerName}</TableCell><TableCell>{row.moverName}</TableCell><TableCell>{money(row.amount)}</TableCell><TableCell><Chip label={row.paymentStatus} size="small" color={statusColor(row.paymentStatus)} /></TableCell><TableCell><Chip label={row.settlementStatus} size="small" color={statusColor(row.settlementStatus)} /></TableCell><TableCell>{row.failureReason || 'None'}</TableCell></TableRow>)}</TableBody></Table></TableContainer>
              </Paper>
            </Stack>
          )}

          {tab === 5 && (
            <Paper elevation={0} sx={{ p: 2.5, border: '1px solid #D9E2EC', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                <Box>
                  <Typography variant="h6" fontWeight={800}>Admin Audit Log</Typography>
                  <Typography variant="body2" color="text.secondary">Recent admin actions across funds, disputes, and mover controls.</Typography>
                </Box>
                <Button variant="outlined" onClick={loadAuditLogs}>Refresh Log</Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow><TableCell>Date</TableCell><TableCell>Admin</TableCell><TableCell>Action</TableCell><TableCell>Target</TableCell><TableCell>Summary</TableCell></TableRow></TableHead>
                  <TableBody>
                    {auditLogs.map(log => <TableRow key={log._id} hover><TableCell>{dateLabel(log.createdAt)}</TableCell><TableCell>{log.admin?.email || 'Admin'}</TableCell><TableCell><Chip label={log.action} size="small" color="primary" variant="outlined" /></TableCell><TableCell>{log.targetType} {log.targetId?.slice?.(-8) || ''}</TableCell><TableCell>{log.summary}</TableCell></TableRow>)}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      <Dialog open={!!selectedPayment} onClose={() => setSelectedPayment(null)} fullWidth maxWidth="sm">
        <DialogTitle>{paymentAction === 'release' ? 'Release Escrow Funds' : paymentAction === 'refund' ? 'Refund Customer' : 'Place Payment On Hold'}</DialogTitle>
        {selectedPayment && <DialogContent dividers><Stack spacing={2}><Alert severity={paymentAction === 'release' ? 'warning' : paymentAction === 'refund' ? 'error' : 'info'} icon={<Warning />}>{paymentAction === 'release' ? 'This creates a Stripe transfer to the mover and marks the booking paid/completed. Only release after completion is verified and no dispute is open.' : paymentAction === 'refund' ? 'This creates a Stripe refund and marks the booking refunded. Use this only after a MOVD review.' : 'This marks escrow as disputed so funds cannot release until MOVD resolves the issue.'}</Alert><Box><Typography fontWeight={800}>{selectedPayment.booking?.jobNumber || selectedPayment._id}</Typography><Typography color="text.secondary">{selectedPayment.mover?.name || 'Mover'} receives {money(selectedPayment.moverAmount)}. MOVD retains {money(selectedPayment.platformCommission)}.</Typography></Box><Divider /><Typography variant="body2">Customer: {selectedPayment.booking?.contactName || selectedPayment.user?.name || 'Customer'}</Typography><Typography variant="body2">Total paid: {money(selectedPayment.totalAmount || selectedPayment.amount)}</Typography><Typography variant="body2">Escrow status: {selectedPayment.escrowStatus}</Typography>{paymentAction !== 'release' && <TextField label="Reason" value={actionReason} onChange={e => setActionReason(e.target.value)} fullWidth multiline minRows={2} />}{paymentAction === 'refund' && <TextField label="Refund amount" type="number" value={refundAmount} onChange={e => setRefundAmount(e.target.value)} fullWidth />}</Stack></DialogContent>}
        <DialogActions><Button onClick={() => setSelectedPayment(null)}>Cancel</Button><Button color={paymentAction === 'release' ? 'success' : paymentAction === 'refund' ? 'error' : 'warning'} variant="contained" startIcon={<CheckCircle />} onClick={runPaymentAction} disabled={!!actionLoading}>{paymentAction === 'release' ? 'Release Funds' : paymentAction === 'refund' ? 'Issue Refund' : 'Place Hold'}</Button></DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default AdminOperationsDashboard;
